import { ReactElement } from 'react';
import { Card, CardContent, CardActions } from '@mui/material';
import { useAwsEffect, useAwsGetter, useRoutes } from '@localstack/services';
import { LambdaUpdateFunctionCodeCommandInput } from '@localstack/types';
import { ProgressButton, LambdaFunctionUpdateCodeForm, PageTitle } from '@localstack/ui';
import { useParams } from 'react-router-dom';

import { LambdaProps } from './types';

export const LambdaFunctionUpdateCode = ({ Layout, clientOverrides, routes }: LambdaProps): ReactElement => {
  const { goto } = useRoutes();
  const { name: functionNameWithVersion } = useParams<'name'>();
  const [functionNameWithoutVersion] = functionNameWithVersion?.split(':') ?? [];

  const { data: lambdaFunction } = useAwsGetter('Lambda', 'getFunction', [{ FunctionName: functionNameWithVersion }], {
    clientOverrides,
  });
  const { updateFunctionCode, isLoading } = useAwsEffect('Lambda', ['updateFunctionCode'], {
    revalidate: ['listFunctions', 'getFunction'],
    clientOverrides,
  });

  const handleCreate = async (data: LambdaUpdateFunctionCodeCommandInput) => {
    if (await updateFunctionCode(data)) {
      goto(routes.RESOURCES_LAMBDA_FUNCTION, { name: data.FunctionName });
    }
  };

  return (
    <Layout
      documentTitle="Lambda: Update Event Function Code"
      title={
        <PageTitle
          title="Update Event Function Code"
          breadcrumbs={[
            ['Lambda', () => goto(routes.RESOURCES_LAMBDA)],
            ['Event Functions', () => goto(routes.RESOURCES_LAMBDA_FUNCTIONS)],
            ['Update Code ', null],
          ]}
        />
      }
      actions={
        <ProgressButton
          type="submit"
          form="UpdateLambdaFunctionCode"
          variant="outlined"
          color="primary"
          loading={isLoading}
        >
          Submit
        </ProgressButton>
      }
    >
      <Card>
        <CardContent>
          {lambdaFunction && (
            <LambdaFunctionUpdateCodeForm
              onSubmit={handleCreate}
              formId="UpdateLambdaFunctionCode"
              loading={isLoading}
              clientOverrides={clientOverrides}
              lambdaFunction={{
                packageType: lambdaFunction?.Configuration?.PackageType,
                name: functionNameWithoutVersion,
              }}
            />
          )}
        </CardContent>
        <CardActions>
          <ProgressButton
            type="submit"
            form="UpdateLambdaFunctionCode"
            variant="contained"
            color="primary"
            loading={isLoading}
          >
            Submit
          </ProgressButton>
        </CardActions>
      </Card>
    </Layout>
  );
};
